import { request, download, fetchBlob } from "./api";
export type Ticket = {
  unread_count?: number;
  owner_email?: string;
  id: string;
  owner_id: string;
  subject: string;
  category: string;
  status: string;
  transfer_amount: string | null;
  transfer_reference: string | null;
  transfer_review: string | null;
  created_at: string;
  updated_at: string;
};
export type Thread = Ticket & {
  has_older: boolean;
  messages: {
    id: number;
    body: string;
    is_operator: boolean;
    created_at: string;
  }[];
  attachments: {
    id: string;
    filename: string;
    size_bytes: number;
    content_type: string;
    created_at: string;
  }[];
};
export type SupportSession = {
  id: string;
  customer_id: string;
  customer_email: string;
  expires_at: string;
};
export type Engagement = {
  id: string; expert_id: string; project_id: string | null; project_title: string;
  status: string; is_client: boolean; rating: number | null; review: string | null;
  public_title: boolean; completed_at: string | null;
};
export type TrackRecord = {
  lifetime_projects: number; completed_projects: number; active_projects: number;
  rating_count: number; average_rating: number | null;
  history: { project_title: string; completed_at: string; rating: number | null; review: string | null }[];
};
export type Expert = {
  id: string;
  display_name: string;
  bio: string;
  specialties: string[];
  languages: string[];
  portfolio_url: string | null;
  experience_years: number;
  availability: string;
  certified_at: string | null;
};
export type ExpertApplication = Expert & {
  status: string;
  listed: boolean;
  review_note: string | null;
  email?: string;
  user_id?: string;
};
const json = (method: string, body?: unknown) => ({
  method,
  body: body === undefined ? undefined : JSON.stringify(body),
});
export const support = {
  tickets: (operator = false, query = "", status = "", offset = 0) =>
    request<Ticket[]>(`/v1/support${operator ? "/operator" : ""}?q=${encodeURIComponent(query)}&status=${encodeURIComponent(status)}&offset=${offset}`),
  markRead: (id: string, message_id: number) => request(`/v1/support/${id}/read`, json("POST", { message_id })),
  create: (body: unknown) => request<Ticket>("/v1/support", json("POST", body)),
  thread: (id: string, before?: number) =>
    request<Thread>(`/v1/support/${id}${before ? `?before=${before}` : ""}`),
  send: (id: string, message: string) =>
    request(`/v1/support/${id}/messages`, json("POST", { message })),
  upload: (id: string, file: File) =>
    request(
      `/v1/support/${id}/attachments?filename=${encodeURIComponent(file.name)}`,
      {
        method: "POST",
        headers: { "Content-Type": "application/octet-stream" },
        body: file,
      },
    ),
  picture: (id: string, attachment: string) => fetchBlob(`/v1/support/${id}/attachments/${attachment}`),
  download: (id: string, attachment: string, filename: string) =>
    download(`/v1/support/${id}/attachments/${attachment}`, filename),
  update: (id: string, body: unknown) =>
    request(`/v1/support/${id}`, json("PATCH", body)),
  startSession: (customer_id: string, reason: string, ticket_id?: string) =>
    request<SupportSession>(
      "/v1/admin/support-sessions",
      json("POST", { customer_id, reason, ticket_id }),
    ),
  stopSession: (id: string) =>
    request(`/v1/admin/support-sessions/${id}`, json("DELETE")),
  resource: <T>(
    session: string,
    path: string,
    method = "GET",
    body?: unknown,
  ) =>
    request<T>(path, {
      ...json(method, body),
      headers: { "X-Support-Session": session },
    }),
};
export const experts = {
  engagements: () => request<Engagement[]>("/v1/experts/engagements/mine"),
  complete: (id: string, public_title: boolean) => request(`/v1/experts/engagements/${id}/complete`, json("POST", { public_title })),
  rate: (id: string, rating: number, review: string) => request(`/v1/experts/engagements/${id}/rating`, json("PUT", { rating, review })),
  directory: () => request<(Expert & TrackRecord)[]>("/v1/experts"),
  application: () =>
    request<ExpertApplication | null>("/v1/experts/application"),
  submit: (body: unknown) =>
    request("/v1/experts/application", json("PUT", body)),
  availability: (availability: string, listed: boolean) =>
    request("/v1/experts/application", json("PATCH", { availability, listed })),
  applications: () =>
    request<ExpertApplication[]>("/v1/experts/operator/applications"),
  review: (id: string, status: string, note: string) =>
    request(
      `/v1/experts/operator/applications/${id}`,
      json("PATCH", { status, note }),
    ),
  invite: (id: string, project_id: string, role: string) =>
    request(`/v1/experts/${id}/invite`, json("POST", { project_id, role })),
  pending: () =>
    request<
      { id: string; project_name: string; role: string; expires_at: string }[]
    >("/v1/experts/invitations/pending"),
  accept: (id: string) =>
    request<{ project_id: string }>(
      `/v1/experts/invitations/${id}/accept`,
      json("POST"),
    ),
};
